import { z } from "zod";
import {
  completionItemsToTaskKeys,
  completionSelectionSchema,
} from "./completion-picker";
import {
  labSampleCostSchema,
  type LabReviewOutcome,
  type LabSampleCost,
} from "./labs";
import {
  DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
  doctorNoticeBodySchema,
  doctorNoticeUnconfirmedClaims,
  finalizeDoctorNotice,
} from "./doctor-notice";
import type { FollowUpItem } from "./medical-evidence";
import { approveOnboardingBodySchema } from "./prescription-cycle";
import {
  WEBDOC_LAB_ORDER_UNCONFIRMED,
  webdocControlSchema,
  webdocLabOrderConfirmed,
} from "./prescription-choice";
import { createSubscriptionLinkSchema } from "./subscription-plan";
import { confirmPrescriptionChoiceSchema } from "./treatment-start";

// TES-952 (TES-941 §WEBDOC OCH BEHANDLINGSBESLUT) — ONE doctor action:
// "Lämna svar till patienten och fatta behandlingsbeslut".
//
// Emelie + Kirill 2026-09-25:
//   "Det ska inte finnas ett separat steg där Kirill först markerar provsvaret som granskat."
// There is no Webdoc integration, so Ella cannot know that a result has come
// in. Kirill reads it in Webdoc, and his first act in Ella is this decision.
// Before it there were three: "Provsvar har kommit in" (TES-506),
// "Provsvar granskat" with three outcomes (TES-890) and "Godkänn"/"Avslå" in the header,
// plus the subscription link as a fourth, free-standing header action.
//
// Ö-242's assumption (a): the review stamp and its outcome are written
// IMPLICITLY when the decision is published. So publishing writes exactly the
// columns the old buttons wrote (`resultsReceivedAt`, `reviewedAt`,
// `reviewOutcome`, `requestedEvidenceTasks`), and every reader of them —
// the activation gate, `deriveBloodTestStatus`, the labs lane, the CP-06
// case, `followUpOutstanding` — keeps its meaning without knowing this
// action exists.

export const TREATMENT_DECISION_OUTCOMES = [
  "approve",
  "completion",
  "decline",
] as const;
export const treatmentDecisionOutcomeChoiceSchema = z.enum(
  TREATMENT_DECISION_OUTCOMES,
);
export type TreatmentDecisionOutcomeChoice = z.infer<
  typeof treatmentDecisionOutcomeChoiceSchema
>;

// Her words (TES-941), as the three choices in the workspace.
export const TREATMENT_DECISION_OUTCOME_LABEL: Record<
  TreatmentDecisionOutcomeChoice,
  string
> = {
  approve: "Godkänn behandling",
  completion: "Begär ytterligare komplettering",
  decline: "Avslå / avstå från behandling",
};

// Ö-245 answered (a) via TES-978: the decision offers the two plans under
// their product names, "Ella Uppstart" / "Ella Underhåll"
// (`SUBSCRIPTION_PLAN_LABEL`). The earlier workspace-only labels
// "Uppstart" / "Uppföljning" are gone, so there is no third name for a plan.

// The patient-facing answer. Required whenever a lab result is answered:
// "Lämna svar till patienten" is half of the action's name, and a decision on
// a blood test that tells her nothing about it is the gap this closes.
// TES-1201 — it is "Läkarens besked till patienten · visas under Din vård",
// published as a medical notice (no longer a doctor chat message), and it may
// not claim a remiss, lab order or prescription before its Webdoc
// confirmation (doctorNoticeUnconfirmedClaims, refined below).
const patientSummary = doctorNoticeBodySchema;

const common = {
  // The case's lab order the decision answers. Absent when the decision is
  // taken straight after the meeting with the evidence already complete.
  labOrderId: z.string().uuid().optional(),
  patientSummary: patientSummary.optional(),
  // TES-1071 — an approval also needs Steg B's two further ticks
  // (positiveDecisionTicksConfirmed), checked by the API the same way.
  // TES-1055 (Emelie, point H) — every outcome is published with
  // `journalInWebdoc`, as the transfer and monthly reviews are:
  // "bekräftar att journalanteckning och ordination är införda i Webdoc".
  // Only then are prescribedAt and the pickup task written. Checked by the
  // API (400 journal_not_in_webdoc) rather than refined here, so the refusal
  // carries its own messageKey. `labOrderDoneInWebdoc` (TES-997, Ö-259) is
  // also required when a komplettering asks for a new blood test.
  webdocControl: webdocControlSchema.optional(),
  // TES-1061 — the version of the decision's journal note the doctor copied
  // ("…/treatment-decision/note/finalize"). The journal tick is a receipt on
  // it: the API refuses the publish (409) unless it is the note's newest
  // final version and still compiles to exactly the text of this decision.
  // Required whenever the patient has a note to write the decision into.
  noteRevisionId: z.string().uuid().optional(),
};

// TES-1185 — Emelie's two choices, verbatim (TES-1180 point 1).
export const PLANNED_TREATMENT_CHOICES = ["kept", "changed"] as const;
export const PLANNED_TREATMENT_CHOICE_LABEL: Record<
  (typeof PLANNED_TREATMENT_CHOICES)[number],
  string
> = {
  kept: "Planen kvarstår",
  changed: "Ändra planerad läkemedelsbehandling",
};
export const plannedTreatmentChoiceSchema = z
  .object({
    choice: z.enum(PLANNED_TREATMENT_CHOICES),
    // "Om planen ändras ska orsak dokumenteras."
    reason: z.string().trim().max(2000).optional(),
    // TES-1221 (A-02 rules 2 and 5) — on a change, "styrka" and
    // "behandlingsplan" are part of the new version too; the order's four
    // fields are the approval's. Left out, the current version's value
    // carries over; "" clears it. A kept plan changes neither.
    strength: z.string().trim().max(64).optional(),
    plan: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.choice === "changed" && !v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "plan_change_reason_required",
      });
    }
    if (v.choice === "kept" && v.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason"],
        message: "plan_change_reason_not_applicable",
      });
    }
    if (
      v.choice === "kept" &&
      (v.strength !== undefined || v.plan !== undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [v.strength !== undefined ? "strength" : "plan"],
        message: "plan_change_fields_not_applicable",
      });
    }
  });
export type PlannedTreatmentChoice = z.infer<
  typeof plannedTreatmentChoiceSchema
>;

const approveDecision = z.object({
  ...common,
  outcome: z.literal("approve"),
  // The approval body: the PLANNED order, the mandatory clinical phase
  // (TES-841) and the follow-up confirmation (TES-736). The prescription
  // status is not part of it (`prescription_not_at_decision`): it is the
  // decision's own `prescription` below.
  approval: approveOnboardingBodySchema,
  // TES-1185 (TES-1180 points 1, 2 and 4) — the prescription status, in the
  // same publish: "Receptet är utfärdat i Webdoc" or "Befintligt recept
  // gäller fortfarande". Registered on the pending first cycle exactly as
  // TES-1150's separate "Bekräfta receptstatus" step did, so a new patient
  // never waits in that step's queue. The verified subscription still
  // releases it.
  // TES-1221 (A-02 rule 9) — required: the prescription is confirmed before
  // activation, so a positive decision without it is refused
  // (`prescription_choice_required`) instead of falling back to the old
  // two-step order. The separate step stays for the transition queue only.
  prescription: confirmPrescriptionChoiceSchema,
  // TES-1185 (TES-1180 points 1 and 4) — after kompletteringar, the plan
  // saved at the meeting is shown prefilled and Kirill chooses "Planen
  // kvarstår" or "Ändra planerad läkemedelsbehandling"; a change carries its
  // medical reason. Journaled in the decision's note.
  // TES-1221 (A-02 rule 5) — the API requires it when a planned medication
  // is on record and the decision follows requested kompletteringar
  // (`planned_treatment_choice_required`). Without kompletteringar "kept"
  // is refused, and an order that differs from the plan must be "changed"
  // with its reason. The meeting's own one-step decision is exempt.
  plannedTreatment: plannedTreatmentChoiceSchema.optional(),
  // TES-907 (Ö-221): an explicit plan, nothing pre-selected — the same
  // schema the free-standing link used, so "inget förvalt" and the
  // maintenance plan's basis rule hold here too.
  subscription: createSubscriptionLinkSchema,
});

const completionDecision = z.object({
  ...common,
  outcome: z.literal("completion"),
  // TES-950's picker, exactly as every other entry point sends it.
  completion: completionSelectionSchema,
  // Ö-227: whether a new blood test is paid. Required exactly when one is
  // asked for, and never defaulted.
  sampleCost: labSampleCostSchema.optional(),
  // Internal, never shown to the patient (as on the lab review).
  reason: z.string().trim().max(2000).optional(),
});

const declineDecision = z.object({
  ...common,
  outcome: z.literal("decline"),
  // Kept encrypted on the rejected cycle and on the CP-09 notice (TES-1035),
  // and journaled (TES-1061).
  reason: z.string().trim().min(1).max(2000),
  // TES-1061 (TES-1069 point 13) — "Fortsatt vårdväg", journaled with the
  // motivation.
  carePath: z.string().trim().max(2000).optional(),
});

export const publishTreatmentDecisionSchema = z
  .discriminatedUnion("outcome", [
    approveDecision,
    completionDecision,
    declineDecision,
  ])
  .superRefine((v, ctx) => {
    // TES-1201 — a notice of prefilled headings only is no answer.
    if (v.labOrderId && !finalizeDoctorNotice(v.patientSummary ?? "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["patientSummary"],
        message: "patient_summary_required",
      });
    }
    // TES-1201 — the notice says nothing exists before its Webdoc
    // confirmation. TES-1185 — a positive decision now carries "Receptet är
    // utfärdat i Webdoc", which confirms the prescription. Whether she may be
    // TOLD before her subscription is active is the API's check (A-02: no
    // copy that reveals the prescription before subscription_activated).
    if (
      v.patientSummary &&
      doctorNoticeUnconfirmedClaims(v.patientSummary, {
        labOrder: webdocLabOrderConfirmed(v.webdocControl),
        prescription:
          v.outcome === "approve" && v.prescription?.choice === "new_issued",
      }).length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["patientSummary"],
        message: DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
      });
    }
    if (v.outcome !== "completion") return;
    // A komplettering is asked on a result. Straight after a meeting it is
    // asked in the meeting's own documentation, which opens the CP-06 case.
    if (!v.labOrderId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["labOrderId"],
        message: "completion_requires_lab_order",
      });
    }
    const newSample = v.completion.items.includes("new_blood_test");
    if (newSample && !webdocLabOrderConfirmed(v.webdocControl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["webdocControl", "labOrderDoneInWebdoc"],
        message: WEBDOC_LAB_ORDER_UNCONFIRMED,
      });
    }
    if (newSample && !v.sampleCost) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sampleCost"],
        message: "sample_cost_required",
      });
    }
    if (!newSample && v.sampleCost) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sampleCost"],
        message: "sample_cost_not_applicable",
      });
    }
  });
export type PublishTreatmentDecision = z.infer<
  typeof publishTreatmentDecisionSchema
>;

export interface ImplicitLabReview {
  outcome: LabReviewOutcome;
  sampleCost?: LabSampleCost;
  requestedTasks: string[];
}

// The lab review the decision writes (Ö-242). One mapping, so the stamp is
// the same whatever the admin shows:
//   approve    → "Godkänt underlag": opens the activation gate.
//   decline    → "Godkänt underlag" too. The result was sufficient to decide;
//                the decision was no. Treatment ends, so the gate is moot, and
//                recording "komplettering krävs" would open a CP-06 case for
//                a patient who has just been declined.
//   completion → "Nytt prov krävs" when a new blood test is among the items
//                (Ö-227's cost comes with it), otherwise
//                "Komplettering krävs" — the same derivation the TES-950 lab
//                review used.
export function implicitLabReview(
  decision: PublishTreatmentDecision,
): ImplicitLabReview {
  if (decision.outcome !== "completion") {
    return { outcome: "approved", requestedTasks: [] };
  }
  const requestedTasks = completionItemsToTaskKeys(decision.completion.items, {
    labPayment: false,
  });
  if (decision.completion.items.includes("new_blood_test")) {
    return {
      outcome: "new_sample_required",
      ...(decision.sampleCost ? { sampleCost: decision.sampleCost } : {}),
      requestedTasks,
    };
  }
  return { outcome: "completion_required", requestedTasks };
}

// The follow-up the doctor is asked to confirm, as it will stand AFTER the
// decision answers a lab result. The page's list is read before publishing,
// so it still says the result is unreviewed and the sample not given — both
// facts the decision itself settles. Asking him to confirm them would be
// asking him to acknowledge his own act as outstanding. The server
// recomputes the list after the stamp and refuses if anything else is open,
// so this only ever removes what the stamp removes.
const SETTLED_BY_LAB_ANSWER: ReadonlySet<FollowUpItem> = new Set<FollowUpItem>([
  "labs_not_reviewed",
  "labs_completion_required",
  "labs_new_sample_required",
  "new_blood_test",
]);

export function followUpAfterLabAnswer(
  outstanding: readonly FollowUpItem[],
  answersLab: boolean,
): FollowUpItem[] {
  return answersLab
    ? outstanding.filter((i) => !SETTLED_BY_LAB_ANSWER.has(i))
    : [...outstanding];
}

export const publishTreatmentDecisionResultSchema = z.object({
  outcome: treatmentDecisionOutcomeChoiceSchema,
  treatmentState: z.string().nullable(),
  labOrderId: z.string().uuid().nullable(),
  reviewOutcome: z.string().nullable(),
  // Present on approval: the link the doctor may also share in the call.
  subscriptionLink: z
    .object({
      url: z.string(),
      expiresAt: z.string(),
      emailed: z.boolean(),
      plan: z.string(),
    })
    .nullable(),
  // TES-1185 — the prescription status was registered in this publish.
  prescriptionRegistered: z.boolean().optional(),
});
export type PublishTreatmentDecisionResult = z.infer<
  typeof publishTreatmentDecisionResultSchema
>;
